import React from 'react';

interface SkeletonProps {
  /** Tailwind class string for sizing (e.g., 'h-4 w-32'). */
  className?: string;
  /** Use a softer pulse instead of the shimmer sweep. */
  pulse?: boolean;
  /** Stagger animation delay in ms — looks great on lists/grids. */
  delay?: number;
}

/**
 * Skeleton primitive. A rounded gray block with a brand-tinted shimmer that
 * sweeps left-to-right. Pair multiple at sizes that mirror the actual content
 * shape so the loading state previews what's about to appear.
 *
 * Respects `prefers-reduced-motion` (animation is suppressed → static block).
 */
export const Skeleton: React.FC<SkeletonProps> = ({
  className = 'h-4 w-full',
  pulse = false,
  delay = 0,
}) => {
  const style: React.CSSProperties =
    delay > 0 ? { animationDelay: `${delay}ms` } : {};

  if (pulse) {
    return (
      <div
        className={`bg-primary-200 rounded-md animate-skeleton-pulse motion-reduce:animate-none ${className}`}
        style={style}
        aria-hidden="true"
      />
    );
  }

  // .skeleton-block (defined in index.css) supplies a darker base, a bright
  // white-to-brand-orange shimmer sweep, and a pulsing brand glow on the
  // outer ring — visually unmistakable as "loading".
  return (
    <div
      className={`skeleton-block ${className}`}
      style={style}
      aria-hidden="true"
    />
  );
};

/**
 * Visible "Loading..." pill — pulses softly in brand orange with a ping
 * indicator so the loading state is unambiguous, even at a glance from
 * across the room.
 */
export const LoadingPill: React.FC<{ label?: string }> = ({
  label = 'Loading',
}) => (
  <div
    className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full bg-brand-50 border border-brand-300 text-brand-700 text-sm font-bold uppercase tracking-wider shadow-medium animate-pulse"
    role="status"
    aria-live="polite"
  >
    <span className="relative flex h-2.5 w-2.5" aria-hidden="true">
      <span className="absolute inline-flex h-full w-full rounded-full bg-brand-500 opacity-75 animate-ping" />
      <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-brand-600" />
    </span>
    <span>{label}</span>
  </div>
);

/**
 * Skeleton matching the dashboard StatCard component shape.
 */
export const StatCardSkeleton: React.FC<{ delay?: number }> = ({ delay = 0 }) => (
  <div
    className="stat-card p-8 flex items-center"
    aria-hidden="true"
  >
    <Skeleton className="h-16 w-16 rounded-xl mr-6" delay={delay} />
    <div className="flex-1 space-y-3">
      <Skeleton className="h-3 w-24" delay={delay + 100} />
      <Skeleton className="h-8 w-16" delay={delay + 200} />
    </div>
  </div>
);

/**
 * Skeleton for a list of cards/rows. Renders `count` placeholder rows that
 * stagger their shimmer for a "ripple" effect.
 */
export const ListSkeleton: React.FC<{ count?: number; rowHeight?: string }> = ({
  count = 4,
  rowHeight = 'h-16',
}) => (
  <div className="space-y-3" aria-hidden="true">
    {Array.from({ length: count }).map((_, i) => (
      <div
        key={i}
        className={`bg-white border border-primary-200 rounded-xl p-4 flex items-center gap-4 ${rowHeight}`}
      >
        <Skeleton className="h-10 w-10 rounded-full" delay={i * 80} />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-3 w-1/3" delay={i * 80 + 100} />
          <Skeleton className="h-3 w-2/3" delay={i * 80 + 200} />
        </div>
        <Skeleton className="h-8 w-20 rounded-md" delay={i * 80 + 300} />
      </div>
    ))}
  </div>
);

/**
 * Skeleton for a generic data table. Renders `rows` rows × `cols` columns
 * with a header row above.
 */
export const TableSkeleton: React.FC<{ rows?: number; cols?: number }> = ({
  rows = 6,
  cols = 5,
}) => (
  <div
    className="bg-white border border-primary-200 rounded-xl overflow-hidden"
    aria-hidden="true"
  >
    <div className="grid gap-4 p-4 border-b border-primary-200 bg-primary-50" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
      {Array.from({ length: cols }).map((_, c) => (
        <Skeleton key={c} className="h-3 w-full max-w-[80%]" delay={c * 60} />
      ))}
    </div>
    {Array.from({ length: rows }).map((_, r) => (
      <div
        key={r}
        className="grid gap-4 p-4 border-b last:border-b-0 border-primary-100"
        style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
      >
        {Array.from({ length: cols }).map((_, c) => (
          <Skeleton
            key={c}
            className={`h-4 ${c === 0 ? 'w-3/4' : c === cols - 1 ? 'w-1/2' : 'w-full max-w-[85%]'}`}
            delay={r * 70 + c * 30}
          />
        ))}
      </div>
    ))}
  </div>
);

/**
 * Skeleton for a hero/banner section — large title block + subtitle.
 */
export const HeroSkeleton: React.FC = () => (
  <div className="space-y-4" aria-hidden="true">
    <Skeleton className="h-10 w-2/3 max-w-md" />
    <Skeleton className="h-4 w-1/2 max-w-sm" delay={120} />
  </div>
);

export default Skeleton;
